import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import "./Autocomplete.scss";

import _ from "lodash";
import { useValidation } from "../utils/ValidationProvider";

const SORT = [];
const PAGE = {
  skip: 0,
  take: 10,
};

let _timer;

const showedField = "stringToShow";

/**
 * Autocomplete input
 * @module
 * @param {string} textField - Text field
 * @param {string} [valueField] - Value field, used for regex
 * @param {object} [selected] - Selected item
 * @param {func} onChange - Function invoked on field change
 * @param {array} data - All data
 * @param {bool} isLoading - Loading
 * @param {func} onSelect - Function invoked on item select
 * @param {func} onFocus - Fires each time the user focuses the AutoComplete.
 * @param {object} [filter] - Filter
 * @param {func} [onFilter] - On filter Function
 * @param {bool} [required] - Field is required
 * @param {bool} [disabled] - Field is disabled
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {bool} [autofocus] - Should input be focused?
 * @param {string} [textFilter] - Text filter
 * @param {string} [placeholder] - Placeholder for input
 * @param {number} [tabIndex] - Tabindex for input
 * @param {string} [name] - field name
 * @param {array} [sort] - Array of sorts
 */
const AutocompleteComponent = (props) => {
  const autoCompleteRef = useRef(null);
  const data = props.allDataItem ? [props.allDataItem, ...props.data] : props.data;
  const textField = props.valueField && props.valueField !== props.textField ? showedField : props.textField;
  const [open, setOpen] = useState(undefined);

  //tests
  useEffect(() => {
    if (autoCompleteRef.current && autoCompleteRef.current.element && props.name) {
      autoCompleteRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name]);

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue: setValidation } = useValidation(field, props.selected);
  const setValidationValue = props.setValidation || setValidation; //Custom picker validation

  const buildTextField = (item) => {
    if (item) {
      if (props.valueField) {
        let toReplace = props.valueField.match(/{([^}]+)}/g);
        if (!toReplace) {
          return _.get(item, props.valueField);
        }
        let finalString = props.valueField;
        toReplace.forEach((element) => {
          let propName = element.replace(/[{}]/g, "");
          const replaceWith = _.get(item, propName);
          finalString = finalString.replace(
            element,
            replaceWith != null ? replaceWith : ""
          );
        });
        return finalString.trim();
      } else {
        return _.get(item, props.textField);
      }
    }
  };

  const _data = data.map(item => {
    let updatedItem = { ...item };
    if (props.valueField !== props.textField) {
      let text = buildTextField(updatedItem);
      updatedItem = _.set(updatedItem, showedField, text);
    }
    if (!_.get(updatedItem, props.textField)) {
      updatedItem = _.set(updatedItem, props.textField, "");
    }
    return updatedItem;
  });

  const buildFilter = (value) => {
    if (!value) {
      return {
        filters: [],
      };
    }
    return {
      logic: "and",
      filters: [
        {
          field: props.textField,
          operator: "contains",
          value,
          ignoreCase: true,
        },
      ],
    };
  };

  const handleOpen = (e) => {
    if (e.syntheticEvent.type === "click" && !e.target.value) {
      setOpen(false);
    } else {
      setOpen(undefined);
    }
  };

  const onClose = async () => {
    validate();
    await props.onBlur;
    const value = autoCompleteRef.current._input.value;
    if (value && !props.selected?.[props.primaryField]) {
      data.forEach((object) => {
        if (_.get(object, textField.split('.')) === value && !_.isEqual(object, props.selected)) {
          props.onSelect(object, props.name);
        }
      });
    }
    if (props.onBlur) {
      props.onBlur(props.selected);
    }
  };

  const onChange = async (event) => {
    const filter = buildFilter(event.target.value);
    setValidationValue(event.target.value);

    if (props.onFilter) {
      props.onFilter(filter);
    }

    const eventType = event.nativeEvent.type;
    if (
      (eventType === "click" && event.target.value !== '') ||
      (eventType === "keydown" && event.nativeEvent.keyCode === 13)
    ) {
      const value = _data.find(
        (x) => {
          return _.get(x, textField) === event.target.value;
        }
      );
      await props.onSelect(value, props.name);
      setValidationValue(value);
      if (props.onBlur) {
        await props.onBlur(value);
      }
    } else {
      if ((props.selected !== undefined && props.selected !== null) || props.allDataItem) {
        setValidationValue(null);
        await props.onSelect(null, props.name, event);
        if (props.onBlur) {
          await props.onBlur(null);
        }
      }

      if (_timer) {
        clearTimeout(_timer);
      }
      _timer = setTimeout(async () => {
        props.onChange(filter, props.sort ?? SORT, PAGE);
      }, 500);
    }
  };

  const buildValue = () => {
    if (props.textFilter !== undefined) {
      return props.textFilter;
    }

    if (props.selected) {
      if (props.selected[showedField]) {
        return props.selected[showedField];
      }
      if (props.valueField) {
        let toReplace = props.valueField.match(/{([^}]+)}/g);
        if (!toReplace) {
          return _.get(props.selected, props.valueField.split('.'));
        }
        let finalString = props.valueField;
        toReplace.forEach((element) => {
          let propName = element.replace(/[{}]/g, "");
          const replaceWith = _.get(props.selected, propName);
          finalString = finalString.replace(
            element,
            replaceWith != null ? replaceWith : ""
          );
        });
        return finalString.trim();
      } else {
        return _.get(props.selected, textField.split('.'));
      }
    }

    const filter = props.filter;
    if (!filter) {
      return undefined;
    }
    const f = filter.filters.find((x) => x.field === props.textField);
    return f && f.value ? f.value.toString() : "";
  };


  useEffect(() => {
    if (props.autofocus) {
      autoCompleteRef.current._input.focus();
    }
  }, [props.autofocus]);

  const className = "autocomplete " + (props.disabled ? "disabled-field " : "") + (props.className ? props.className : "") + (props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field"));

  return (<>
    <AutoComplete
      className={className}
      validationMessage={props.validationMessage || "Povinné"}
      data={_data}
      onChange={onChange}
      onBlur={onClose}
      textField={textField}
      loading={props.isLoading}
      value={buildValue()}
      required={props.required}
      disabled={props.disabled}
      placeholder={props.placeholder}
      ref={autoCompleteRef}
      tabIndex={props.tabIndex}
      footer={props.footer}
      onFocus={props.onFocus}
      onOpen={handleOpen}
      opened={open}
    />
  </>
  );
};

AutocompleteComponent.propTypes = {
  className: PropTypes.string,
  textField: PropTypes.string.isRequired,
  valueField: PropTypes.string,
  primaryField: PropTypes.string.isRequired,
  selected: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  filter: PropTypes.object,
  onFilter: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  autofocus: PropTypes.bool,
  textFilter: PropTypes.string,
  placeholder: PropTypes.string,
  tabIndex: PropTypes.number,
  name: PropTypes.string,
  allDataItem: PropTypes.object,
  sort: PropTypes.array
};

export default AutocompleteComponent;